import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ThemeProperties } from "../Theme/ThemeProperties";

import resolutionIcon   from "../Assets/Icons/BMSFaults/alertCardResolution.svg";
import expirationIcon   from "../Assets/Icons/BMSFaults/alertCardExpiration.svg";
import persistenceIcon  from "../Assets/Icons/BMSFaults/alertCardPersistence.svg";
import cooldownIcon     from "../Assets/Icons/BMSFaults/alertCardCooldown.svg";
import descriptionIcon  from "../Assets/Icons/BMSFaults/alertCardDescription.svg";
import causeOfFaultIcon from "../Assets/Icons/BMSFaults/alertCardCauseOfFault.svg";
import instructionsIcon from "../Assets/Icons/BMSFaults/alertCardInstructions.svg";

const T = ThemeProperties;

const SEVERITY = [
  { key: "critical",     label: "Critical",      dot: T.alertSeverity_criticalDot, bg: T.alertSeverity_criticalBg },
  { key: "highSeverity", label: "High Severity", dot: T.alertSeverity_highDot,     bg: T.alertSeverity_highBg     },
  { key: "warning",      label: "Warning",       dot: T.alertSeverity_warningDot,  bg: T.alertSeverity_warningBg  },
];

const CONDITION_ROWS = (alert) => [
  { icon: resolutionIcon,  label: "Resolution Condition", value: alert.resolutionCondition },
  { icon: expirationIcon,  label: "Expiration Condition",  value: alert.expirationCondition },
  { icon: persistenceIcon, label: "Persistence Duration",  value: alert.persistenceDuration },
  { icon: cooldownIcon,    label: "Cooldown Duration",     value: alert.cooldownDuration },
];

const DETAIL_SECTIONS = [
  { icon: descriptionIcon,  key: "description",  label: "Description" },
  { icon: causeOfFaultIcon, key: "causeOfFault", label: "Cause of Fault" },
  { icon: instructionsIcon, key: "instructions", label: "Instructions" },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function DetailBlock({ icon, label, children }) {
  return (
    <Box sx={{ bgcolor: T.alertCard_detailBg, borderRadius: "9px", p: "14px 16px" }}>
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: "8px",
          fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em",
          textTransform: "uppercase", color: T.alertCard_ink3, mb: "8px",
        }}
      >
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <img src={icon} alt={label} width={14} height={14} />
        </Box>
        {label}
      </Box>
      {children}
    </Box>
  );
}

DetailBlock.propTypes = {
  icon:     PropTypes.string.isRequired,
  label:    PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function InlineDetail({ severity, details }) {
  return (
    <Box
      sx={{
        mt: "13px",
        "@keyframes detailIn": {
          from: { opacity: 0, transform: "translateY(-4px)" },
          to:   { opacity: 1, transform: "none" },
        },
        animation: "detailIn .22s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "12px" }}>
        <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: severity.dot, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.alertCard_ink }}>
          {severity.label}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {DETAIL_SECTIONS.map(({ icon, key, label }) => {
          const content = details[key];
          const isEmpty = !content || (Array.isArray(content) && content.length === 0);
          return (
            <DetailBlock key={key} icon={icon} label={label}>
              {isEmpty ? (
                <Typography sx={{ m: 0, fontSize: "13.5px", lineHeight: 1.5, color: T.alertCard_ink3 }}>
                  -
                </Typography>
              ) : Array.isArray(content) ? (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {content.map((item, i) => (
                    <Box key={`${i}-${item}`} component="li" sx={{ display: "flex", gap: "10px" }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: T.alertCard_ink4, mt: "7px", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "13.5px", lineHeight: 1.45, color: T.alertCard_ink2 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ m: 0, fontSize: "13.5px", lineHeight: 1.5, color: T.alertCard_ink2 }}>
                  {content}
                </Typography>
              )}
            </DetailBlock>
          );
        })}
      </Box>
    </Box>
  );
}

InlineDetail.propTypes = {
  severity: PropTypes.shape({ dot: PropTypes.string, label: PropTypes.string }).isRequired,
  details:  PropTypes.shape({
    description:  PropTypes.string,
    causeOfFault: PropTypes.string,
    instructions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

// ── Main component ────────────────────────────────────────────────────────────

function AlertConfigCard({ alert, onToggle, onFetchSeverityDetails }) {
  const [activeSev, setActiveSev] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  const activeSevMeta = SEVERITY.find((s) => s.key === activeSev);
  const hasDetails = Boolean(onFetchSeverityDetails) || Boolean(alert.severityDetails);
  const resolvedProtocols = alert.protocols?.length
    ? alert.protocols
    : alert.protocolName
    ? [alert.protocolName]
    : [];

  const activeSevDetails = activeSev
    ? (onFetchSeverityDetails ? detailsCache[activeSev] : alert.severityDetails?.[activeSev])
    : null;

  const handleSevClick = useCallback((key) => {
    if (activeSev === key) {
      setActiveSev(null);
      return;
    }
    setActiveSev(key);
    if (onFetchSeverityDetails && !(key in detailsCache)) {
      setLoadingKey(key);
      onFetchSeverityDetails(key)
        .then((details) => {
          setDetailsCache((prev) => ({ ...prev, [key]: details }));
          setLoadingKey((prev) => (prev === key ? null : prev));
        })
        .catch(() => {
          setDetailsCache((prev) => ({ ...prev, [key]: null }));
          setLoadingKey((prev) => (prev === key ? null : prev));
        });
    }
  }, [activeSev, detailsCache, onFetchSeverityDetails]);

  return (
    <Box data-testid={`alert-card-${alert.id}`} sx={{ border: `1px solid ${T.alertCard_line}`, borderRadius: "12px", p: "20px 22px", bgcolor: "#fff", fontFamily: T.primaryFontV2 }}>

      {/* ── Row 1: title · protocol badge ───────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: T.alertCard_ink, lineHeight: "24px" }}>
          {alert.alertName}
        </Typography>
        {resolvedProtocols.map((name) => (
          <Box
            key={name}
            sx={{
              fontSize: "11.5px", fontWeight: 700,
              color: T.protocol_text,
              bgcolor: T.protocol_bg,
              border: `1.5px solid ${T.protocol_border}`,
              borderRadius: "6px", px: "10px", py: "3px",
              letterSpacing: "0.04em", whiteSpace: "nowrap",
            }}
          >
            {name}
          </Box>
        ))}
      </Box>

      {/* ── Row 2: tags ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: "9px", mt: "13px", mb: "16px", flexWrap: "wrap" }}>
        {[
          { label: "Alert Type",      value: alert.alertType },
          { label: "Entity Affected", value: alert.batteryEntity },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              bgcolor: T.alertCard_tagBg,
              border: `1px solid ${T.alertCard_line}`,
              fontSize: "12.5px",
              px: "13px", py: "5px",
              borderRadius: "7px",
              display: "flex", alignItems: "center", gap: "5px",
            }}
          >
            <Box component="span" sx={{ color: T.alertCard_ink3, fontWeight: 400 }}>{label}:</Box>
            <Box component="span" sx={{ color: T.alertCard_ink,  fontWeight: 700 }}>{value}</Box>
          </Box>
        ))}
      </Box>

      {/* ── Row 3: severity boxes (clickable) — only rendered when condition data exists ── */}
      {(() => {
        const visibleSeverities = SEVERITY.filter((s) => alert.severity?.[s.key]);
        if (visibleSeverities.length === 0) return null;
        return (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "13px" }}>
            {visibleSeverities.map((sev) => {
              const isActive = activeSev === sev.key;
              return (
                <Box
                  key={sev.key}
                  data-testid={`severity-${sev.key}-${alert.id}`}
                  onClick={hasDetails ? () => handleSevClick(sev.key) : undefined}
                  sx={{
                    bgcolor: sev.bg, borderRadius: "9px", px: "17px", py: "15px",
                    cursor: hasDetails ? "pointer" : "default", userSelect: "none",
                    boxShadow: isActive ? `0 0 0 2px ${sev.dot}` : "none",
                    transition: "box-shadow .15s, transform .15s",
                    ...(hasDetails && {
                      "&:hover": {
                        boxShadow: isActive ? `0 0 0 2px ${sev.dot}` : `0 0 0 1.5px ${sev.dot}`,
                        transform: "translateY(-1px)",
                      },
                      "&:active": { transform: "translateY(1px)" },
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "9px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: sev.dot, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: T.alertCard_ink }}>
                        {sev.label}
                      </Typography>
                    </Box>
                    {hasDetails && (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 17,
                          color: sev.dot,
                          opacity: isActive ? 1 : 0.45,
                          transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform .2s, opacity .15s",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: "13px", color: T.alertCard_ink3, lineHeight: "18px" }}>
                    {alert.severity[sev.key]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* ── Inline detail (accordion) ───────────────────────────────────── */}
      {activeSev && activeSevMeta && (
        loadingKey === activeSev ? (
          <Box data-testid={`severity-loading-${alert.id}`} sx={{ display: "flex", justifyContent: "center", py: "18px" }}>
            <CircularProgress size={22} sx={{ color: activeSevMeta.dot }} />
          </Box>
        ) : activeSevDetails ? (
          <Box data-testid={`severity-detail-section-${alert.id}`}>
            <InlineDetail key={activeSev} severity={activeSevMeta} details={activeSevDetails} />
          </Box>
        ) : (
          <Box data-testid={`severity-empty-section-${alert.id}`} sx={{ mt: "13px", display: "flex", justifyContent: "center", alignItems: "center", py: "28px", bgcolor: T.alertCard_detailBg, borderRadius: "9px" }}>
            <Typography sx={{ fontSize: "13px", color: T.alertCard_ink3 }}>No Information Available</Typography>
          </Box>
        )
      )}

      {/* ── Row 4: condition cards (2×2 grid) ───────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "13px", mt: "13px" }}>
        {CONDITION_ROWS(alert).map(({ icon, label, value }) => (
          <Box
            key={label}
            sx={{ border: `1px solid ${T.alertCard_line}`, borderRadius: "9px", px: "15px", py: "13px", display: "flex", alignItems: "center", gap: "11px" }}
          >
            <Box sx={{ width: 18, height: 18, flexShrink: 0, display: "flex" }}>
              <img src={icon} alt={label} width={18} height={18} />
            </Box>
            <Typography sx={{ fontSize: "13px", color: T.alertCard_ink3, lineHeight: "18px" }}>
              <Box component="span" sx={{ fontWeight: 600, color: T.alertCard_ink }}>{label}:</Box>{" "}{value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

AlertConfigCard.propTypes = {
  alert: PropTypes.shape({
    id:                  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    alertName:           PropTypes.string.isRequired,
    protocolName:        PropTypes.string,
    protocols:           PropTypes.arrayOf(PropTypes.string),
    alertType:           PropTypes.string.isRequired,
    batteryEntity:       PropTypes.string.isRequired,
    isEnabled:           PropTypes.bool,
    severity:            PropTypes.shape({
      critical:     PropTypes.string,
      highSeverity: PropTypes.string,
      warning:      PropTypes.string,
    }),
    severityDetails:     PropTypes.object,
    resolutionCondition: PropTypes.string,
    expirationCondition: PropTypes.string,
    persistenceDuration: PropTypes.string,
    cooldownDuration:    PropTypes.string,
  }).isRequired,
  onToggle:              PropTypes.func,
  onFetchSeverityDetails: PropTypes.func,
};

export default AlertConfigCard;
